import { Icon, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useMemo, useState } from 'react'

import { SchemaWatemplate } from '@/__generated/es-api'
import TablePagination from '@/app/component/atom/table-pagination'
import CustomTooltip from '@/app/component/atom/tooltip'
import { usePrevious } from '@/app/helpers'

import Row from './row'

type Props = {
  selectedCategories: string[]
  templates: SchemaWatemplate[]
}

const WATemplatesTable: React.FC<Props> = ({ templates, selectedCategories }) => {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(15)

  const handleChangeRowsPerPage = (newSize: number) => {
    setSize(newSize)
    setPage(1)
  }

  // Filter templates based on selected categories
  const filteredTemplates = useMemo(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return templates
    }

    return templates.filter((template) => {
      const templateCategory = template.category || 'Unknown'
      return selectedCategories.includes(templateCategory)
    })
  }, [templates, selectedCategories])

  const prevSelectedCategories = usePrevious(selectedCategories.sort().join(','))
  React.useEffect(() => {
    const currentSelectedCategories = selectedCategories.sort().join(',')
    if (prevSelectedCategories === currentSelectedCategories) {
      return
    }
    setPage(1)
  }, [prevSelectedCategories, selectedCategories])

  const paginatedTemplates = filteredTemplates.slice((page - 1) * size, page * size)

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell width="200px">
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Approved category
                  <CustomTooltip title="This reflects the template's last verified category. Meta may have updated the classification since then. Use 'Check for updates' to retrieve the current status.">
                    <Icon fontSize="small" color="action">
                      info_outline
                    </Icon>
                  </CustomTooltip>
                </div>
              </TableHeadCell>
              <TableHeadCell>Preview</TableHeadCell>
              <TableHeadCell width="170px">Status</TableHeadCell>
              <TableHeadCell width="170px">Creation Date</TableHeadCell>
              <TableHeadCell width="140px">Actions</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTemplates.map((template) => (
              <Row key={template.id} template={template} />
            ))}
            {!paginatedTemplates.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {selectedCategories.length > 0
                      ? 'No templates found for selected categories'
                      : 'No templates found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!!paginatedTemplates.length && (
        <TablePagination
          namespace="templates"
          page={page}
          pageSizeValues={[
            {
              value: 15,
              label: '15',
            },
            {
              value: 25,
              label: '25',
            },
            {
              value: 50,
              label: '50',
            },
          ]}
          size={size}
          total={filteredTemplates.length}
          onPageChange={setPage}
          onPageSizeChange={handleChangeRowsPerPage}
        />
      )}
    </>
  )
}

const TableContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  overflow: 'auto',

  '& table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderTop: 'none',
  },
})

const TableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

export default WATemplatesTable
